import React, {useCallback, useEffect, useMemo, useState} from "react";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {ArrowBack, BallotRounded, ContentCopy, DeleteOutline, EditOutlined} from "@mui/icons-material";
import Button from "../../../../../../../components/base/button/button";
import classnames from "classnames";
import {PanelFooter} from "../../../../../../../components/layout";
import {useDispatch} from "react-redux";
import {setReduxDialogState} from "../../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../../core/models/static/routes/redux-store-routes";
import {PrintBrochureViewSteps} from "../../index";
import {Box, Card, Checkbox, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import useSavedComponentsState from "../../../../../../../hooks/use-saved-components-state";
import Dialog from "../../../../../../../components/base/dialog";
import Form, {MuiRFF, Yup} from "../../../../../../../components/form";
import {createUUId} from "../../../../../../../../core/services/utils/utils";
import ValidateMessages from "../../../../../../../../core/models/static/texts/vallidate-messages";
import {ReactComponent as CheckboxIcon} from "../../../../../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as ActiveCheckboxIcon} from "../../../../../../../../assets/images/check-box/checkbox-active.svg";

const PrintBrochureViewBottomActionBar = ({
                                              step,
                                              productSelectionState,
                                              configureGroupsState,
                                              overrideState,
                                              reviewState,
                                              setStep,
                                              updatingStep,
                                          }) => {
    const dispatch = useDispatch();

    const selectedCategoriesLength = useMemo(() => productSelectionState.selectedCategories.length ?? 0, [productSelectionState.selectedCategories.length]);
    const selectedProductsLength = useMemo(() => productSelectionState.selectedProducts.length ?? 0, [productSelectionState.selectedProducts.length]);
    const selectedTagsLength = useMemo(() => productSelectionState.selectedTags.length ?? 0, [productSelectionState.selectedTags.length]);
    const groupsLength = useMemo(() => configureGroupsState.groups.filter(e => !e.isRemoved).length, [configureGroupsState.groups]);
    const reviewStateProductsLength = useMemo(() => reviewState.groups.reduce((a, e) => a + e.products.filter(e => !e.isRemoved).length, 0), [reviewState.groups]);

    /**@type {boolean}*/
    const show = useMemo(() => {
        let show = false
        switch (step) {
            case PrintBrochureViewSteps.productSelection:
                if (!!selectedCategoriesLength || selectedTagsLength || selectedProductsLength)
                    show = true;
                break;
            default:
                show = true;
                break;
        }
        return show;
    }, [selectedCategoriesLength, selectedProductsLength, selectedTagsLength, step])

    /**@type {string}*/
    const title = useMemo(() => {
        switch (step) {
            case PrintBrochureViewSteps.productSelection:
                return [
                    selectedCategoriesLength ? `${selectedCategoriesLength} ${(selectedCategoriesLength ?? 0) === 1 ? 'Category' : 'Categories'}` : '',
                    selectedProductsLength ? `${selectedProductsLength} ${(selectedProductsLength ?? 0) === 1 ? 'Product' : 'Products'}` : '',
                    !selectedTagsLength ? '' : `${selectedTagsLength} ${(selectedTagsLength ?? 0) === 1 ? 'Tag' : 'Tags'}`,
                ]
                    .filter(Boolean)
                    .join(' and ').concat(' selected');
            case PrintBrochureViewSteps.configureGroups:
                return `${groupsLength} Group${groupsLength === 1 ? '' : 's'}`
                    + ' selected'
            case PrintBrochureViewSteps.review:
                return `${reviewStateProductsLength} ${reviewStateProductsLength === 1 ? "Product" : "Products"} selected`;
            default:
                return "";
        }
    }, [step, selectedCategoriesLength, selectedProductsLength, selectedTagsLength, groupsLength, reviewStateProductsLength])

    /**@type {boolean}*/
    const showPreviousStep = useMemo(() => ![PrintBrochureViewSteps.productSelection].includes(step), [step])

    /**@type {string}*/
    const previousStepTitle = useMemo(() => {
        if (updatingStep)
            return "Processing..."
        switch (step) {
            case PrintBrochureViewSteps.configureGroups:
                return "Back to product selection"
            case PrintBrochureViewSteps.review:
                return "Back to configure groups"
            default:
                return '';

        }
    }, [step, updatingStep])


    /**@type {string}*/
    const nextStepTitle = useMemo(() => {
        if (updatingStep) {
            return "Proceeding..."
        }
        if (step === PrintBrochureViewSteps.review) {
            return "Print";
        }
        return "Next Step";
    }, [step, updatingStep])

    /**
     * Step change handler.
     *
     * If the current step is the review step, a confirmation dialog will be shown to the user.
     * @type {(function(*): void)}
     */
    const onStepChange = useCallback((newStep) => {
        if (step === PrintBrochureViewSteps.configureGroups && step === PrintBrochureViewSteps.productSelection) {
            dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                open: true,
                title: 'Going Back',
                description: 'You will lose any changes made to the products of this poster. Are you sure you want to go back?',
                cancelButtonText: 'Stay',
                confirmButtonText: 'Go back',
                onClose: async (confirmed) => {
                    if (confirmed) {
                        setStep(step, newStep);
                    }
                    return true;
                }
            }));
        } else {
            setStep(step, newStep);
        }
    }, [dispatch, setStep, step])

    /**
     * Navigates the user to the previous step of the request.
     * @return {Function | undefined}
     */
    const setPrevStep = useCallback(() => {
        let newStep;
        switch (step) {
            case PrintBrochureViewSteps.configureGroups:
                newStep = PrintBrochureViewSteps.productSelection;
                break;
            case PrintBrochureViewSteps.review:
                onStepChange(PrintBrochureViewSteps.configureGroups);
                return;
            default:
                break;
        }
        if (newStep) {
            setStep(step, newStep);
        }
    }, [onStepChange, setStep, step])

    /**
     * Navigates the user to the next step of the request.
     * @return {Function | undefined}
     */
    const setNextStep = useCallback(() => {
        let newStep;
        switch (step) {
            case PrintBrochureViewSteps.productSelection:
                newStep = PrintBrochureViewSteps.configureGroups;
                break;
            case PrintBrochureViewSteps.configureGroups:
                newStep = PrintBrochureViewSteps.review;
                break;
            case PrintBrochureViewSteps.review:
                newStep = 'print';
                break;
            default:
                break;
        }
        if (newStep) {
            setStep(step, newStep);
        }
    }, [setStep, step])

    return (
        <>
            <PanelFooter>
                <div className={'bottom-action-bar'}>
                    <div className={classnames('bottom-action-bar-content', {'with-previous-step': showPreviousStep})}>
                        <div className={'d-flex flex-column align-items-start'}>
                            <p>
                                {title}
                            </p>
                            {
                                showPreviousStep &&
                                <div className={'previous-step'}>
                                    <IconButton
                                        disabled={updatingStep}
                                        onPressed={setPrevStep}
                                        color={'paper.secondary'}
                                    >
                                        <ArrowBack className='icon small'/>
                                    </IconButton>
                                    <p>
                                        {previousStepTitle}
                                    </p>
                                </div>
                            }
                        </div>
                        <div className={'d-flex align-items-start flex-shrink-0'}>
                            <TemplateManagement
                                step={step}
                                productSelectionState={productSelectionState}
                                configureGroupsState={configureGroupsState}
                                canSave={show}
                                overrideState={overrideState}
                            />
                            {
                                show &&
                                <Button
                                    disabled={updatingStep}
                                    onClick={setNextStep}
                                    variant={'contained'}
                                    size={'medium'}
                                    color={'primary'}
                                    sx={{minWidth: 120}}
                                >
                                    {nextStepTitle}
                                </Button>
                            }
                        </div>
                    </div>
                </div>
            </PanelFooter>
        </>
    );
};

export default PrintBrochureViewBottomActionBar;


const TemplateSavedComponentStateInfo = {name: "PrintBrochureTemplates", version: '1.0.0'};

const initTemplateManagementDialogState = {open: false, title: "", action: "", onSelect: () => void 0, close: () => void 0};

const TemplateManagement = ({
                                productSelectionState,
                                configureGroupsState,
                                canSave,
                                overrideState,
                            }) => {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [templateManagementDialogState, setTemplateManagementDialogState] = useState(initTemplateManagementDialogState);
    const dispatch = useDispatch();
    const isMounted = useIsMounted();

    const {highestVersion: getCachedTemplates, save: saveCachedTemplates} = useSavedComponentsState(TemplateSavedComponentStateInfo);

    /**
     * Fetches the templates from the server and saves them to the cache.
     * @type {(function(boolean, boolean): Promise<*>)}
     */
    const fetchTemplates = useCallback(async (loading = true, returnData = false) => {
        if (loading) {
            setLoadingTemplates(true);
        }
        const cache = await getCachedTemplates();
        if (!isMounted())
            return;
        setTemplates(cache?.data ?? []);
        if (loading) {
            setLoadingTemplates(false);
        }
        if (returnData)
            return cache?.data ?? [];
    }, [isMounted, getCachedTemplates])

    /**
     * Fetches the templates on the component mount.
     */
    useEffect(() => {
        void fetchTemplates();
    }, [fetchTemplates]);

    /**
     * Saves the current configuration in the selected template and selects it.
     * @type {(function(*): void)}
     */
    const saveDataInTemplateAndSelect = useCallback(async (template, shouldSelect) => {
        const newTemplate = {...template, data: {productSelectionState, configureGroupsState}}
        await saveCachedTemplates((templates) => templates?.some(e => e.id === template.id)
            ? templates?.map(e => e.id === template.id ? newTemplate : e)
            : [...(templates ?? []), newTemplate]);
        await fetchTemplates(false, false);
        if (!isMounted())
            return;
        if (shouldSelect) {
            setSelectedTemplateId(template.id);
        }
    }, [configureGroupsState, fetchTemplates, isMounted, productSelectionState, saveCachedTemplates]);

    /**
     * Opens the template selection dialog.
     * @type {(function(): void)}
     */
    const openSaveDataDialog = useCallback(() => {
        setTemplateManagementDialogState({
            open: true,
            title: "Save Current Configuration",
            action: "Save",
            selectedTemplateId: selectedTemplateId,
            onSelect: (template) => void saveDataInTemplateAndSelect(template, true),
            close: () => {
                void fetchTemplates(false, false);
                setTemplateManagementDialogState(initTemplateManagementDialogState)
            },
        });
    }, [fetchTemplates, saveDataInTemplateAndSelect, selectedTemplateId]);

    /**
     * Selects the template and applies its data to the current configuration.
     * @type {(function(*): void)|*}
     */
    const selectTemplateAndApplyData = useCallback(async (template) => {
        if (!!selectedTemplateId || canSave) {
            const confirmed = await new Promise(resolve => {
                dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                    open: true,
                    title: 'Selecting Template',
                    description: `You are about to select the template "${template.name}". This will override your current configuration with the one saved in the template. Do you want to proceed?`,
                    maxWidth: 'sm',
                    fullWidth: true,
                    onClose: (confirmed) => {
                        resolve(confirmed);
                        return true;
                    }
                }));
            });
            if (!confirmed)
                return;
        }
        setSelectedTemplateId(template.id);
        overrideState(template.data)
    }, [canSave, dispatch, overrideState, selectedTemplateId])

    /**
     * Opens the template selection dialog for selecting a template to apply its data to the current configuration.
     * @type {(function(): void)|*}
     */
    const openTemplateSelectionDialog = useCallback(() => {
        setTemplateManagementDialogState({
            open: true,
            title: "Select Template",
            action: "Select",
            onSelect: selectTemplateAndApplyData,
            close: () => {
                void fetchTemplates(false, false);
                setTemplateManagementDialogState(initTemplateManagementDialogState)
            },
        });
    }, [selectTemplateAndApplyData, fetchTemplates])

    return (
        <>
            {
                !loadingTemplates &&
                <Box sx={{display: 'flex', alignItems: 'center', mr: 1, gap: 1}}>
                    {
                        canSave &&
                        <Button variant={'text'} color={'primary'} onClick={openSaveDataDialog}>
                            Save Changes
                        </Button>
                    }
                    {
                        templates.length > 0 &&
                        <Card
                            onClick={openTemplateSelectionDialog}
                            variant={'elevation'}
                            sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                boxShadow: 'none',
                                backgroundColor: 'background.default',
                                borderRadius: 1,
                                padding: 1,
                            }}
                        >
                            <BallotRounded color={'primary'}/>
                            <Typography variant={'caption'} component={'div'} fontWeight={600}>
                                {templates.find(e => e.id === selectedTemplateId)?.name ?? "Select template"}
                            </Typography>
                        </Card>
                    }
                </Box>
            }
            <TemplateManagementDialog
                open={templateManagementDialogState.open}
                title={templateManagementDialogState.title}
                action={templateManagementDialogState.action}
                selectedTemplateId={selectedTemplateId}
                templates={templates}
                onSelect={templateManagementDialogState.onSelect}
                onClose={templateManagementDialogState.close}
                refreshTemplates={fetchTemplates}
            />
        </>
    );
}


const TemplateManagementDialog = ({open, title, action, selectedTemplateId, onSelect, onClose, templates, refreshTemplates}) => {
    const dispatch = useDispatch();
    const {save: saveCachedTemplates} = useSavedComponentsState(TemplateSavedComponentStateInfo);
    const [selectedEntry, setSelectedEntry] = useState(selectedTemplateId);
    const [upsertDialogState, setUpsertDialogState] = useState({open: false, template: null});

    /**
     * Sets the selected template ID to the one passed as a prop when the dialog is opened or when the selected template ID changes.
     */
    useEffect(() => {
        if (open) {
            setSelectedEntry(selectedTemplateId);
        }
    }, [selectedTemplateId, open]);

    /**
     * Deletes the template after confirming with the user and updates the state and cache.
     * @type {(function(*): void)}
     */
    const handleDelete = useCallback((template) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: `template: "${template.name}"`,
            onClose: async (confirmed) => {
                if (confirmed) {
                    await saveCachedTemplates(prevState => prevState?.filter(t => t.id !== template.id) ?? []);
                    if (selectedEntry === template.id) {
                        setSelectedEntry(null);
                    }
                    await refreshTemplates();
                }
                return true;
            }
        }));
    }, [dispatch, refreshTemplates, saveCachedTemplates, selectedEntry]);

    /**
     * Duplicates the template by creating a new template with the same data and a different ID and name, then saves it to the cache and updates the state.
     * @type {(function(*): Promise<void>)}
     */
    const handleDuplicate = useCallback(async (template) => {
        const newTemplate = {...template, id: createUUId(), name: `${template.name} (Copy)`};
        await saveCachedTemplates(prevState => [...(prevState ?? []), newTemplate]);
        await refreshTemplates();
    }, [saveCachedTemplates, refreshTemplates]);

    /**
     * Handles the select button click by applying the selected template's data to the current configuration and closing the dialog.
     * @type {(function(): void)|*}
     */
    const handleSelectClick = useCallback(() => {
        if (selectedEntry && onSelect) {
            onSelect(templates.find(e => e.id === selectedEntry));
        }
        onClose();
    }, [onClose, onSelect, selectedEntry, templates]);

    const sortedTemplates = useMemo(() => {
        const result = [...templates];
        result.sort((a, b) => {
            if (a.id === selectedTemplateId) return -1;
            if (b.id === selectedTemplateId) return 1;
            return 0;
        })
        return result;
    }, [selectedTemplateId, templates]);

    return (
        <>
            <Dialog open={open} setOpen={onClose} maxWidth={'sm'} fullWidth>
                <DialogTitle fontWeight={600} variant={'subtitle1'}>
                    {title || "Manage Templates"}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, marginBlock: 2}}>
                        {
                            sortedTemplates.map((template) => (
                                <Card
                                    key={template.id}
                                    variant={'outlined'}
                                    onClick={() => setSelectedEntry(prevState => prevState === template.id ? null : template.id)}
                                    sx={{
                                        padding: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        cursor: 'pointer',
                                        ...(selectedEntry === template.id && {
                                            backgroundColor: theme => `rgba(${theme.vars.palette.secondary.mainChannel} / 0.12)`,
                                            borderColor: 'secondary.main',
                                        })
                                    }}
                                >
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                        {
                                            !!onSelect &&
                                            <Checkbox
                                                checked={selectedEntry === template.id}
                                                size={'small'}
                                                sx={{padding: 0}}
                                                checkedIcon={<ActiveCheckboxIcon/>}
                                                icon={<CheckboxIcon/>}
                                            />
                                        }
                                        <Typography variant="body2" fontWeight={500}>
                                            {template.name}
                                        </Typography>
                                        {
                                            selectedTemplateId === template.id &&
                                            <Typography variant="caption" color="primary" fontStyle={'italic'}>
                                                (Currently Selected)
                                            </Typography>
                                        }
                                    </Box>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                        <IconButton
                                            size={'small'}
                                            color={'primary'}
                                            onPressed={(e) => e.stopPropagation() || e.preventDefault() || setUpsertDialogState({open: true, template})}
                                        >
                                            <EditOutlined className={'icon normal'}/>
                                        </IconButton>
                                        <IconButton
                                            size={'small'}
                                            color={'primary'}
                                            onPressed={(e) => e.stopPropagation() || e.preventDefault() || handleDuplicate(template)}
                                        >
                                            <ContentCopy className={'icon small'}/>
                                        </IconButton>
                                        <IconButton
                                            size={'small'}
                                            color={'error'}
                                            onPressed={(e) => e.stopPropagation() || e.preventDefault() || handleDelete(template)}
                                        >
                                            <DeleteOutline className={'icon normal'}/>
                                        </IconButton>
                                    </Box>
                                </Card>
                            ))
                        }
                        {
                            sortedTemplates.length === 0 &&
                            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{my: 10}}>
                                No templates found. please create a new template by clicking the "Create New" button below and saving your current configuration.
                            </Typography>
                        }
                    </Box>
                </DialogContent>
                <DialogActions className={'m-0 p-0'}>
                    <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                        <Button
                            variant={'dialog'}
                            color={'primary'}
                            onClick={onClose}
                            borderRadius={'0 8px 0 8px'}
                        >
                            Close
                        </Button>
                        <Box sx={{display: 'flex'}}>
                            <Button
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => setUpsertDialogState({open: true, template: null})}
                                borderRadius={!!onSelect && sortedTemplates.length > 0 ? '8px 0 0 0' : '8px 0 8px 0'}
                            >
                                Create New
                            </Button>
                            {
                                !!onSelect && sortedTemplates.length > 0 &&
                                <Button
                                    variant={'dialog'}
                                    color={'primary'}
                                    onClick={handleSelectClick}
                                    disabled={!selectedEntry}
                                    borderRadius={'0 0 8px 0'}
                                >
                                    {action || "Select"}
                                </Button>
                            }
                        </Box>
                    </Box>
                </DialogActions>
            </Dialog>
            <UpsertTemplateDialog
                open={upsertDialogState.open}
                template={upsertDialogState.template}
                refreshTemplates={refreshTemplates}
                close={() => setUpsertDialogState({open: false, template: null})}
            />
        </>
    );
}

const UpsertTemplateDialog = ({open, template, refreshTemplates, close}) => {
    const {save: saveCachedTemplates} = useSavedComponentsState(TemplateSavedComponentStateInfo);
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const formId = useMemo(() => createUUId(), []);

    const schema = useMemo(() => Yup.object().shape({
        name: Yup.string().trim().required(ValidateMessages.required),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        name: template?.name || '',
    }), [template]);

    /**
     * Handles the form submission by saving the new or updated template to the cache and refreshing the templates list, then closes the dialog.
     * @type {(function(*): Promise<void>)}
     */
    const submitForm = useCallback(async (values) => {
        setSubmitting(true);
        const newTemplate = template ? {...template, name: values.name} : {id: createUUId(), name: values.name, data: {}};
        await saveCachedTemplates((templates) => {
            const temp = templates ?? [];
            if (template) {
                return temp.map(e => e.id === template.id ? newTemplate : e);
            } else {
                return [...temp, newTemplate];
            }
        });
        if (refreshTemplates) {
            await refreshTemplates(false, false);
        }
        if (!isMounted()) return;
        setSubmitting(false);
        close();
    }, [template, saveCachedTemplates, refreshTemplates, isMounted, close]);

    return (
        <Dialog open={open} setOpen={close} maxWidth={'sm'} fullWidth>
            <DialogTitle>
                {template ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
            <DialogContent dividers>
                <Form
                    onSubmit={submitForm}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{id: formId}}
                >
                    <Box paddingBlock={4}>
                        <MuiRFF.TextField
                            name={'name'}
                            label={'Name'}
                            required={required.name}
                            fullWidth
                        />
                    </Box>
                </Form>
            </DialogContent>
            <DialogActions className={'m-0 p-0'}>
                <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => close()}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        form={formId}
                        type={'submit'}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {submitting ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}